// @flow
import React, { Component } from 'react';
import Checkbox from 'react-bootstrap/lib/Checkbox';
import CheckFacet from './CheckboxFacet';
import classNames from 'classnames';
import type { Distance as DistanceType } from '../../types/Distance';
import Facet from './Facet';
import type { FacetOption as FacetOptionType } from '../../types/FacetOption';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import isEqual from 'react-fast-compare';
import RadioFacet from './RadioFacet';
import ScrollBar from '../../components/ScrollBar/ScrollBar';
import SeeMoreItems from '../../components/SeeMoreItems/SeeMoreItems';
import SelectedCountry from './SelectedCountry';
import styles from './LocationShippingFacet.module.scss';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import toggleStyles from './ToggleStyles.module.scss';

const DISTANCE_COUNTRIES = ['', 'US'];
const DISTANCE_OPTIONS = [20, 40, 60, 80, 100, 120, 250, 500, 1000];
const distanceOptions = DISTANCE_OPTIONS.map((option, index) => {
    return (
        <option value={option} key={index}>
            {option}
        </option>
    );
});
const MAX_HEIGHT_FOR_CHECKS = 400;
const MULTISELECT_FACET_LIMIT = 4;

type Props = {
    availableCountries: FacetOptionType[],
    countries: FacetOptionType[],
    distance: DistanceType,
    distanceSelected: Function,
    facetOptionChecked: Function,
    loadPageWithFacetCollapsed: boolean,
    removeCountryAndDistance: Function,
    replaceCountry: Function,
    shippingFacet: any,
    title: string,
};

type State = {
    changeCountry: boolean,
    range: number,
    showAllCountries: boolean,
    showScrollBar: boolean,
    withinChecked: boolean,
    zipCode: string,
    zipCodeValid: boolean,
};

export default class LocationShippingFacet extends Component<Props, State> {
    state = {
        changeCountry: false,
        range: 20,
        showAllCountries: false,
        showScrollBar: false,
        withinChecked: false,
        zipCode: '',
        zipCodeValid: true,
    };

    componentDidMount() {
        if (this.props.distance) {
            this.updateDistanceState(this.props.distance);
        }
    }

    shouldComponentUpdate = (nextProps: Props, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: Props) {
        if (!isEqual(prevProps.distance, this.props.distance)) {
            this.updateDistanceState(this.props.distance);
        }
    }

    updateDistanceState = (distance: DistanceType) => {
        if (distance?.zipCode) {
            this.setState({
                range: distance?.space,
                showScrollBar: false,
                withinChecked: Boolean(distance?.zipCode),
                zipCode: distance?.zipCode,
                zipCodeValid: true,
            });
        } else {
            this.setState({
                range: 20,
                showScrollBar: false,
                withinChecked: false,
                zipCode: '',
                zipCodeValid: true,
            });
        }
    };

    onCountryRevealClick = () => {
        this.setState(state => ({ changeCountry: !state.changeCountry }));
    };

    onSeeAllCountriesClick = () => {
        this.setState(state => ({ showScrollBar: !state.showScrollBar }));
    };

    onChangeCountry = (event: SyntheticInputEvent<>) => {
        const id = event.target.value;
        if (!id.length) {
            this.anywhereCheck(event);
        } else {
            this.props.replaceCountry(id);
            this.setState({ changeCountry: false, showScrollBar: false });
        }
    };

    checkForShipping = (event: SyntheticInputEvent<>) => {
        const id = event.target.value;
        this.props.facetOptionChecked('shipping', id, event.target.checked);
    };

    distanceSelected = (event: SyntheticInputEvent<>) => {
        this.setState({ range: Number(event.target.value) });
    };

    zipChanged = (event: SyntheticInputEvent<>) => {
        const zip = event.target.value;
        if (zip.length <= 5 && (!isNaN(zip) || zip === '')) {
            this.setState({ withinChecked: true, zipCode: zip });
        }
    };

    distanceClickHandler = () => {
        const country = 'US';
        const measurement = 'mile';

        const zipCodeValid = this.state.zipCode.length === 5;
        this.setState({ zipCodeValid });

        if (zipCodeValid) {
            this.setState({ changeCountry: false, showScrollBar: false, withinChecked: true });
            this.props.distanceSelected(country, this.state.zipCode, this.state.range, measurement);
        }
    };

    distanceEnterKeyHandler = (event: SyntheticKeyboardEvent<>) => {
        if (event.key === 'Enter') {
            this.distanceClickHandler();
        }
    };

    withinCheck = (event: SyntheticInputEvent<>) => {
        const withinChecked = event.target.checked;
        this.setState({ changeCountry: false, showScrollBar: false, withinChecked });
        if (!withinChecked) {
            this.setState({ zipCode: '', zipCodeValid: true });
            this.props.removeCountryAndDistance();
        }
    };

    anywhereCheck = (event: SyntheticInputEvent<>) => {
        this.props.removeCountryAndDistance();
        event.target.checked = true;
        this.setState({ changeCountry: false, showScrollBar: false });
    };

    createCountryRadio = (option: FacetOptionType) => {
        return <RadioFacet key={option.id} onChange={this.onChangeCountry} option={option} />;
    };

    createShippingCheckbox = (option: FacetOptionType) => {
        return <CheckFacet key={option.id} onChange={this.checkForShipping} option={option} />;
    };

    renderSelectedCountry = (option: FacetOptionType) => {
        const linkText = <FormattedMessage id="searchChangeCountry" />;
        return (
            <SelectedCountry key={option.id} linkText={linkText} onChange={this.onCountryRevealClick} option={option} />
        );
    };

    customCountryOrder = (countries: FacetOptionType[]) => {
        // places these countries at the head of a new list
        const filtered = ['US', 'UK', 'CA'];
        // $FlowFixMe
        const reordered = countries.filter(c => !filtered.includes(c.id));
        const us = countries.filter(c => c.id === 'US')[0];
        const uk = countries.filter(c => c.id === 'UK')[0];
        const ca = countries.filter(c => c.id === 'CA')[0];
        ca && reordered.unshift(ca);
        uk && reordered.unshift(uk);
        us && reordered.unshift(us);
        return reordered;
    };

    getShippingOptions = ({
        facetOptions,
        showDistance,
    }: {
        facetOptions: FacetOptionType[],
        showDistance: boolean,
    }) => {
        let filteredOptions = facetOptions;
        if (filteredOptions.length) {
            if (showDistance) {
                filteredOptions = filteredOptions.filter(facet => facet.id !== 'freeLocalPickup');
            }
        }
        return filteredOptions.length && filteredOptions.map<any>(this.createShippingCheckbox);
    };

    getLocalPickupOption = (facetOptions: FacetOptionType[]) => {
        const freePickupOption = facetOptions.filter(facet => facet.id === 'freeLocalPickup');
        return freePickupOption.length && freePickupOption[0];
    };

    render() {
        const {
            availableCountries = [],
            countries = [],
            loadPageWithFacetCollapsed,
            shippingFacet,
            title = '',
        } = this.props;
        const { changeCountry, range, showScrollBar, withinChecked, zipCode, zipCodeValid } = this.state;
        const selectedCountry: FacetOptionType = countries.filter(country => country.selected)[0] || {
            id: '',
            name: 'Anywhere',
            selected: true,
        };

        let interimlist = availableCountries.map((country: FacetOptionType) => ({
            ...country,
            selected: country.id === selectedCountry.id,
        }));
        interimlist = this.customCountryOrder(interimlist);
        interimlist.unshift(({ id: '', name: 'Anywhere', selected: !selectedCountry.id.length }: FacetOptionType));
        const countryOptions = interimlist.map(this.createCountryRadio);
        const validationProps = zipCodeValid ? {} : { validationState: 'error' };
        const showDistance = DISTANCE_COUNTRIES.includes(selectedCountry.id);
        const shippingOptions =
            shippingFacet && this.getShippingOptions({ facetOptions: shippingFacet.options, showDistance });
        const localPickupOption =
            showDistance && shippingFacet?.options.length && this.getLocalPickupOption(shippingFacet.options);
        const localPickupCheckbox = localPickupOption && this.createShippingCheckbox(localPickupOption);

        let validationFormattedMessageId;
        if (!zipCodeValid) validationFormattedMessageId = 'you_must_enter_a_valid_zip_code';
        const facetClass = title
            .trim()
            .replace('/', '')
            .replace(/\s+/g, '-')
            .toLowerCase();

        return (
            <Facet
                className={`distanceFacet ${facetClass}`}
                title={title}
                showScrollBar={showScrollBar}
                closeFacet={loadPageWithFacetCollapsed}
            >
                <ScrollBar
                    maxHeight={MAX_HEIGHT_FOR_CHECKS}
                    showScrollBar={showScrollBar}
                    className={styles.scrollArea}
                >
                    <FormGroup>
                        <div className={styles.countriesList}>
                            {changeCountry ? (
                                <SeeMoreItems
                                    items={countryOptions}
                                    showItemCount={MULTISELECT_FACET_LIMIT}
                                    showTotal
                                    onSeeMoreItemsClick={this.onSeeAllCountriesClick}
                                    linkClassName={styles.viewMore}
                                    seeMoreFormattedMessageId="search_distance_view_all_countries"
                                    seeLessFormattedMessageId="search_distance_view_less_countries"
                                />
                            ) : (
                                this.renderSelectedCountry(selectedCountry)
                            )}
                        </div>
                        {showDistance && (
                            <div>
                                <div>
                                    <FormGroup {...validationProps} className={styles.distanceSelection}>
                                        <FormControl
                                            type="text"
                                            placeholder="ZIP Code"
                                            className={styles.zip}
                                            onChange={this.zipChanged}
                                            onKeyPress={this.distanceEnterKeyHandler}
                                            value={zipCode}
                                        />
                                        <div className={styles.iconWrapper}>
                                            <FontAwesomeIcon
                                                className={styles.searchFieldIcon}
                                                icon={faSearch}
                                                onClick={this.distanceClickHandler}
                                            />
                                        </div>
                                    </FormGroup>
                                </div>
                                {!zipCodeValid && (
                                    <div className={styles.response}>
                                        <span className={styles.error}>
                                            <FormattedMessage id={validationFormattedMessageId} />
                                        </span>
                                    </div>
                                )}
                                <div className={styles.withinControls}>
                                    <div className={toggleStyles.toggleOption}>
                                        <Checkbox
                                            className={toggleStyles.toggler}
                                            name="distance"
                                            checked={withinChecked}
                                            onChange={this.withinCheck}
                                        >
                                            <Text secondary className={toggleStyles.optionName}>
                                                <FormattedMessage id="within" />
                                            </Text>
                                        </Checkbox>
                                    </div>
                                    <FormGroup
                                        controlId="formControlsSelect"
                                        className={classNames(styles.small, styles.withinSelection)}
                                    >
                                        <FormControl
                                            componentClass="select"
                                            placeholder="select"
                                            className={styles.select}
                                            onChange={this.distanceSelected}
                                            value={range}
                                        >
                                            {distanceOptions}
                                        </FormControl>
                                    </FormGroup>
                                    <Text secondary className={styles.textColor}>
                                        mi
                                    </Text>
                                </div>
                                {Boolean(localPickupCheckbox) && (
                                    <div className={styles.shippingIndent}>{localPickupCheckbox}</div>
                                )}
                            </div>
                        )}
                        <div className={styles.shippingOptions}>{shippingOptions}</div>
                    </FormGroup>
                </ScrollBar>
            </Facet>
        );
    }
}
